import {
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { HighlightIndices, Node, Option } from '@rivethealth/data/options';
import Fuse, { FuseResult } from 'fuse.js';
import _ from 'lodash';
import { ObservableInput } from 'observable-input';
import {
  BehaviorSubject,
  combineLatest,
  merge,
  Observable,
  Subject,
  Subscription,
} from 'rxjs';
import { map, scan, startWith, tap, withLatestFrom } from 'rxjs/operators';
import { Loadable, success } from '../../common/helpers/loadable';
import { replayLast } from '../rx/util';
import { SingleSelectNodeDirective } from './single-select-node.directive';

/**
 * A single-select with optional text search
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'single-select',
  styleUrls: ['./single-select.component.scss'],
  templateUrl: './single-select.component.html',
})
export class SingleSelectComponent<T = unknown> implements OnInit {
  @Input() error = false;
  @Input() warning = false;

  @Input()
  readonly disableArray = [];

  @ObservableInput()
  @Input('disabled')
  readonly _disabled!: Observable<boolean>;
  defaultedDisabled = this._disabled.pipe(startWith(false));

  @Input() placeholder: string = 'Search...';

  @ObservableInput()
  @Input('filterPositionBottom')
  readonly _filterPositionBottom!: Observable<boolean>;
  defaultedFilterPositionBottom = this._filterPositionBottom.pipe(
    startWith(false),
  );

  @ObservableInput()
  @Input('exact')
  readonly _exact!: Observable<boolean>;
  defaultedExact = this._exact.pipe(startWith(false));

  @ObservableInput()
  @Input('filterable')
  readonly _filterable!: Observable<boolean>;
  defaultedFilterable: Observable<boolean> = this._filterable.pipe(
    startWith(false),
  );

  @Input()
  maxElements: number;

  @ObservableInput()
  @Input('options')
  readonly _options!: Observable<Option[]>;
  defaultedOptions: Observable<Option[]> = this._options.pipe(
    startWith([]),
    map(options => options || []),
  );
  @Input()
  type: 'text' | 'email' | 'number' | 'password' | 'search' | 'tel' | 'url' =
    'text';

  @ObservableInput()
  @Input('selectedOption')
  _selectedOption!: Observable<string>;
  defaultedSelectedOption = this._selectedOption.pipe(startWith(null));

  @ObservableInput()
  @Input('optionsLoadable')
  _optionsLoadable!: Observable<Loadable<{ options: Option[]; count: number }>>;
  defaultedOptionsLoadable = this._optionsLoadable.pipe(startWith(null));

  @ObservableInput()
  @Input()
  readonly dismissOptions!: Observable<number>;

  readonly showOptions = new BehaviorSubject<boolean>(true);

  @ObservableInput()
  @Input('clearFilter')
  clearFilter!: Observable<number>;

  @Input('queryParamName')
  queryParamName: string;

  @Input('noResultsText')
  noResultsText: string;

  @Input('displayId')
  displayId: boolean = false;

  readonly doFilter = new BehaviorSubject<string>('');
  readonly filter = merge(
    this.clearFilter.pipe(
      map(() => {
        return '';
      }),
    ),
    this.doFilter,
  );

  readonly optionsVisibleState = merge(
    this.dismissOptions.pipe(
      map(() => {
        return false;
      }),
    ),
    this.showOptions,
  );

  @Output() readonly filterChange = new EventEmitter<string>();

  @Output() readonly selectedOptionChange = new EventEmitter<string | null>();

  @ContentChild(SingleSelectNodeDirective, { static: false })
  nodeDirective: SingleSelectNodeDirective<T>;

  @ViewChildren('nodeRef', { read: ElementRef }) nodeRef: QueryList<ElementRef>;

  public externalKeyPress = new Subject<string>();
  private internalKeyPress = new Subject<string>();

  private fuzzySearch = combineLatest([
    this.defaultedOptions.pipe(
      map(options => options.filter(option => !option.header)),
    ),
    this.defaultedExact,
    this.defaultedFilterable,
  ]).pipe(
    map(([options, exact, filterable]) => {
      const hasHeaders = !!options.find(o => !!o.header);
      return filterable
        ? new Fuse<Option>(options, {
            keys: ['title', 'subtitle'],
            includeMatches: true,
            shouldSort: !hasHeaders,
            threshold: exact ? 0.0 : 0.6,
          })
        : null;
    }),
  );

  private nodeFromMatch(match: FuseResult<Option>): Node {
    // a node is a clone of the item (option),
    // plus the additional fields to make a Node
    let titleHighlightIndices: HighlightIndices = [];
    let subtitleHighlightIndices: HighlightIndices = [];
    let subtextHighlightIndices: HighlightIndices = [];
    for (const fuseMatch of match.matches || []) {
      switch (fuseMatch.key) {
        case 'title':
          titleHighlightIndices = fuseMatch.indices.concat();
          break;
        case 'subtitle':
          subtitleHighlightIndices = fuseMatch.indices.concat();
          break;
        case 'subtext':
          subtextHighlightIndices = fuseMatch.indices.concat();
      }
    }
    return Object.assign(
      {
        selected: false,
        highlighted: false,
        titleHighlightIndices,
        subtitleHighlightIndices,
        subtextHighlightIndices,
      },
      match.item,
    );
  }

  private nodeFromOption(option: Option): Node {
    return {
      ...option,
      selected: false,
      highlighted: false,
      titleHighlightIndices: [],
      subtitleHighlightIndices: [],
      subtextHighlightIndices: [],
    };
  }

  private addMatchHeaders(
    options: Option[],
    matches: FuseResult<Option>[],
  ): Node[] {
    const matchIds: { [key: string]: boolean } = {};
    for (const match of matches) {
      matchIds[match.item.id] = true;
    }

    const headersByMatchId: { [key: string]: Option } = {};
    let lastHeader: Option | null = null;
    for (const node of options) {
      if (node.header) {
        lastHeader = node;
      }
      if (matchIds[node.id] && !!lastHeader) {
        headersByMatchId[node.id] = lastHeader;
      }
    }

    const result: Node[] = [];
    const seen: { [key: string]: boolean } = {};
    for (const match of matches) {
      const header = headersByMatchId[match.item.id];
      if (!!header && !seen[header.title]) {
        result.push(this.nodeFromOption(header));
        seen[header.title] = true;
      }
      result.push(this.nodeFromMatch(match));
    }
    return result;
  }

  private findMatchedNodes(
    options: Option[],
    filter: string,
    fuzzySearch: Fuse<Option>,
  ): Node[] {
    let matchedNodes: Node[] = [];
    if (filter.length > 0) {
      const matches = fuzzySearch.search(filter);
      if (matches.length) {
        matchedNodes = this.addMatchHeaders(options, matches);
      }
      matchedNodes = _.uniqBy(matchedNodes, 'title');
    } else {
      matchedNodes = options.map(option => this.nodeFromOption(option));
    }
    return matchedNodes;
  }

  matchedNodesLoadable: Observable<Loadable<{ nodes: Node[]; count: number }>> =
    combineLatest([
      this.defaultedOptions,
      this.filter,
      this.defaultedFilterable,
      this.fuzzySearch,
      this.defaultedOptionsLoadable,
    ]).pipe(
      map(([options, filter, filterable, fuzzySearch, loadable]) => {
        if (filterable && loadable) {
          return {
            loading: loadable.loading,
            erred: loadable.erred,
            data:
              loadable.data && loadable.data.options
                ? {
                    nodes: loadable.data.options.map(option =>
                      this.nodeFromOption(option),
                    ),
                    count: loadable.data.count,
                  }
                : null,
          };
        } else if (filterable && fuzzySearch) {
          const matched = this.findMatchedNodes(options, filter, fuzzySearch);
          return {
            loading: false,
            erred: false,
            data: {
              nodes: matched,
              count: matched.length,
            },
          };
        } else {
          const matched = options.map(option => this.nodeFromOption(option));
          return {
            loading: false,
            erred: false,
            data: {
              nodes: matched,
              count: matched.length,
            },
          };
        }
      }),
    );

  nodesLimited: Observable<Node[]> = this.matchedNodesLoadable.pipe(
    success(),
    withLatestFrom(this.defaultedFilterable),
    map(([data, filterable]) => {
      return filterable
        ? (data.nodes || []).slice(0, this.maxElements ?? 100)
        : data.nodes;
    }),
  );

  private getNextIndex(currentIndex: number, nodes: any[]) {
    let newIndex = currentIndex;
    let found = false;
    while (!found) {
      if (newIndex >= nodes.length - 1) {
        return newIndex;
      }
      newIndex += 1;
      found = !nodes[newIndex].disabled && !nodes[newIndex].header;
    }
    return newIndex;
  }

  private getPreviousIndex(currentIndex: number, nodes: any[]) {
    let newIndex = currentIndex;
    let found = false;
    while (!found) {
      if (newIndex <= 0) {
        return newIndex;
      }
      newIndex -= 1;
      found = !nodes[newIndex].disabled && !nodes[newIndex].header;
    }
    return newIndex;
  }

  private currentHighlightedOption: string | null = null;

  private keyPress = merge(this.externalKeyPress, this.internalKeyPress);

  private highlightedOption = merge(
    this.filter.pipe(map(filter => ({ type: 'filter', value: filter }))),
    this.nodesLimited.pipe(map(nodes => ({ type: 'nodes', value: nodes }))),
    this._filterable.pipe(
      map(filterable => ({ type: 'filterable', value: filterable })),
    ),
    this._selectedOption.pipe(
      map(option => ({ type: 'selected', value: option })),
    ),
    this.keyPress.pipe(map(key => ({ type: 'key', value: key }))),
  ).pipe(
    scan<
      { type: string; value: any },
      {
        index: number;
        filter: any;
        filterDirty: boolean;
        filterInit: boolean;
        filterable: any;
        selected: any;
        nodes: any[];
      }
    >(
      (state, event) => {
        switch (event.type) {
          case 'filter':
            if (state.filter !== event.value) {
              if (!state.filter && event.value) {
                state.filterInit = true;
              }
              state.filter = event.value;
              state.filterDirty = true;
            }
            break;
          case 'nodes':
            state.nodes = event.value;
            break;
          case 'filterable':
            state.filterable = event.value;
            break;
          case 'selected':
            state.selected = event.value;
            break;
          case 'key':
            switch (event.value) {
              case 'ArrowDown':
                state.index = this.getNextIndex(state.index, state.nodes);
                break;
              case 'ArrowUp':
                state.index = this.getPreviousIndex(state.index, state.nodes);
                break;
            }
            break;
        }
        if (
          state.filterable === false &&
          state.filterDirty &&
          state.nodes.length
        ) {
          // prefix match
          const matchIndex = state.nodes.findIndex(
            node =>
              state.filter &&
              node.title.toLowerCase().startsWith(state.filter.toLowerCase()),
          );
          if (matchIndex >= 0) {
            state.index = matchIndex;
          }
          state.filterDirty = false;
        }
        if (
          state.nodes.length &&
          state.index === -1 &&
          state.selected !== undefined
        ) {
          // initialize highlight to selected value
          if (state.selected) {
            state.index = state.nodes.findIndex(
              node => node.id === state.selected,
            );
          } else {
            state.index = 0;
          }
        }
        if (
          state.filterable &&
          state.filterDirty &&
          state.nodes.length &&
          state.selected !== undefined &&
          state.filterInit
        ) {
          // tend toward putting the highlight on the first item
          // when they start searching
          state.index = 0;
          state.filterInit = false;
        }
        if (
          state.filterable &&
          state.filterDirty &&
          state.nodes.length &&
          state.selected !== undefined &&
          state.index === -1
        ) {
          // fall back to highlighting the first item
          state.index = 0;
        }
        return state;
      },
      {
        index: -1,
        filter: undefined,
        filterDirty: false,
        filterInit: false,
        filterable: undefined,
        selected: undefined,
        nodes: [],
      },
    ),
    replayLast(),
    map(state =>
      state.nodes.length && state.index >= 0
        ? state.nodes[
            this.getNextIndex(
              Math.min(state.nodes.length - 1, state.index) - 1,
              state.nodes,
            )
          ].id
        : null,
    ),
    tap(option => (this.currentHighlightedOption = option)),
    tap(option => this.scrollSubject.next(undefined)),
    startWith(-1),
  );

  nodes: Observable<Node[]> = combineLatest([
    this.nodesLimited,
    this.highlightedOption,
    this.defaultedSelectedOption,
  ]).pipe(
    map(([nodes, highlightedOption, selectedOption]) => {
      if (!highlightedOption && !selectedOption) {
        return nodes;
      }
      const result: Node[] = [];
      nodes.forEach((node, nodeIndex) => {
        if (node.id === highlightedOption || node.id === selectedOption) {
          result.push({
            ...node,
            selected: node.id === selectedOption,
            highlighted: node.id === highlightedOption,
          });
        } else {
          result.push(node);
        }
      });
      return result;
    }),
  );

  notShownCount: Observable<number> = combineLatest([
    this.matchedNodesLoadable.pipe(success()),
    this.nodesLimited,
  ]).pipe(
    map(([nodes, nodesLimited]) =>
      Math.max(0, nodes.count - nodesLimited.length),
    ),
  );

  @HostListener('keyup', ['$event'])
  @HostListener('keydown', ['$event'])
  keyUp($event: any) {
    const key = $event.key;
    if (key === 'ArrowDown' || key === 'ArrowUp' || key === 'Enter') {
      $event.preventDefault();
      $event.stopPropagation();
    }
    if ($event.type === 'keydown') {
      this.internalKeyPress.next(key);
    }
  }

  private keyHandler = this.keyPress.pipe(
    withLatestFrom(this.defaultedFilterable),
    tap(([key, filterable]) => {
      if (key === 'Tab' || key === 'Enter') {
        this.selectedOptionChange.emit(this.currentHighlightedOption);
        this.doFilter.next('');
      }
      if (!filterable && key.length === 1) {
        this.doFilter.next(this.doFilter.value + key);
      }
    }),
  );

  private keyHandlerSub: Subscription;

  private scrollSubject = new Subject();

  public scrollSelectedIntoView() {
    this.scrollSubject.next(undefined);
  }

  private scrollToIndex(index: number) {
    const doScroll = (index: number) => {
      if (this.nodeRef) {
        const nodeRefs = this.nodeRef.toArray();
        if (nodeRefs.length > index) {
          const el = this.nodeRef.toArray()[index];
          if (el.nativeElement) {
            if (el.nativeElement.scrollIntoView) {
              el.nativeElement.scrollIntoView({ block: 'nearest' });
            } else {
              // for templates, all we have is the container (comment)
              // but we can find the node as nextElementSibling
              el.nativeElement.nextElementSibling.scrollIntoView({
                block: 'nearest',
              });
            }
          }
        }
      }
    };
    if (this.nodeRef) {
      doScroll(index);
    } else {
      setTimeout(() => {
        doScroll(index);
      }, 0);
    }
  }

  private scroller = this.scrollSubject.pipe(
    withLatestFrom(this.highlightedOption, this.nodes),
    tap(([_, option, nodes]) => {
      const index = nodes.findIndex(node => node.id === option);
      if (index >= 0) {
        this.scrollToIndex(index);
      }
    }),
  );

  private scrollerSub: Subscription;

  public onNodeClicked(id: string) {
    this.currentHighlightedOption = id;
    this.selectedOptionChange.emit(id);
  }

  public nodeDisabled(disabled: boolean, index: number) {
    return disabled || (this.disableArray && this.disableArray[index]);
  }

  ngOnInit() {
    this.keyHandlerSub = this.keyHandler.subscribe();
    this.scrollerSub = this.scroller.subscribe();
  }

  ngOnDestroy() {
    if (this.keyHandlerSub) {
      this.keyHandlerSub.unsubscribe();
    }
    if (this.scrollerSub) {
      this.scrollerSub.unsubscribe();
    }
  }

  trackBy(index: number, node: Node): string {
    return node.header ? node.title : node.id.toString();
  }
}
